import type {RangeBoundaries} from "instantsearch.js/es/connectors/range/connectRange";
import {useState, useEffect, useRef} from "react";
import {useRange, type UseRangeProps} from 'react-instantsearch';
import {Panel} from "@/instantsearch/components/Panel";

type FilterRangeSliderProps = UseRangeProps & {
    title: string;
};

const formatEuro = (value?: number) => {
    if (!value) value = 0;
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0
    }).format(value);
};

const parseEuro = (euroString: string) => {
    return Number(euroString.replace(/[^0-9-]/g, ''));
};

export function RangeSlider(props: FilterRangeSliderProps) {
    const {start, range, canRefine, refine} = useRange(props);
    const {min, max} = range;
    const [value, setValue] = useState<RangeBoundaries>([min, max]);
    const percent = useRef<[number, number]>([0, 100]);
    const from = Math.max(min as number, Number.isFinite(start[0]) ? start[0] as number : min as number);
    const to = Math.min(max as number, Number.isFinite(start[1]) ? start[1] as number : max as number);
    useEffect(() => {
        setValue([from, to]);
        forceRender({});
    }, [from, to]);

    useEffect(() => {
        if(!min || !max || !value) return;
        percent.current = [
            (value[0] as number- min) / (max - min) * 100,
            (value[1] as number  - min) / (max - min) * 100,
        ];
    }, [value]);

    const [, forceRender] = useState({});

    const handleChange = (index: number, newValue: unknown) => {
        const numericValue = typeof newValue === 'string' ? parseEuro(newValue) : Number(newValue);
        const updatedValue = [...value];
        updatedValue[index] = numericValue;
        setValue(updatedValue as RangeBoundaries);
        forceRender({});
    };

    const handleChangeEnd = () => {
        refine(value as RangeBoundaries);
    };

    if (max === 0 || !canRefine) return (<></>);

    return (
        <Panel title={props.title}>
            <div className="algoliaRangeSlider">
                <div
                    className="algoliaRangeSlider__inputs"
                    role="group"
                    aria-labelledby="range-slider-label"
                >
          <span>
            <label htmlFor="range-min" className="sr-only">
              Minimum price
            </label>
            <input
                id="range-min"
                type="text"
                min={min}
                max={max}
                aria-label="Minimum price"
                value={formatEuro(value[0])}
                onInput={(e) => handleChange(0, e.currentTarget.value)}
                onBlur={handleChangeEnd}
                onMouseUp={handleChangeEnd}
                onTouchEnd={handleChangeEnd}
                disabled={!canRefine}
            />
          </span>
                    <span aria-hidden="true">–</span>
                    <span>
            <label htmlFor="range-max" className="sr-only">
              Maximum price
            </label>
            <input
                id="range-max"
                type="text"
                min={min}
                max={max}
                aria-label="Maximum price"
                value={formatEuro(value[1])}
                onInput={(e) => handleChange(1, e.currentTarget.value)}
                onBlur={handleChangeEnd}
                onMouseUp={handleChangeEnd}
                onTouchEnd={handleChangeEnd}
                disabled={!canRefine}
            />
          </span>
                </div>

                <div
                    className="algoliaRangeSlider__ranges"
                    role="group"
                    aria-labelledby="range-slider-label"
                >
                    <label id="range-slider-label" className="sr-only">
                        Price range slider from {formatEuro(min)} to {formatEuro(max)}
                    </label>

                    <input
                        id="range-slider-min"
                        className="algoliaRangeSlider__rangeMin"
                        type="range"
                        min={min}
                        max={max}
                        value={value[0]}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuenow={value[0]}
                        aria-label="Minimum price slider"
                        onChange={(e) => handleChange(0, (e.target as HTMLInputElement).value)}
                        onInput={(e) => handleChange(0, (e.target as HTMLInputElement).value)}
                        onMouseUp={handleChangeEnd}
                        onTouchEnd={handleChangeEnd}
                        disabled={!canRefine}
                    />

                    <span
                        className="algoliaRangeSlider__track js-rangeSliderTrack"
                        aria-hidden="true"
                        style={{
                            background: `linear-gradient(to right, var(--main-color-middle-gray) ${percent.current[0]}% , var(--main-color-primary) ${percent.current[0]}% , var(--main-color-primary) ${percent.current[1]}%, var(--main-color-middle-gray) ${percent.current[1]}%)`,
                        }}
                    ></span>

                    <input
                        id="range-slider-max"
                        className="algoliaRangeSlider__rangeMax"
                        type="range"
                        min={min}
                        max={max}
                        value={value[1]}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuenow={value[1]}
                        aria-label="Maximum price slider"
                        onChange={(e) => handleChange(1, (e.target as HTMLInputElement).value)}
                        onInput={(e) => handleChange(1, (e.target as HTMLInputElement).value)}
                        onMouseUp={handleChangeEnd}
                        onTouchEnd={handleChangeEnd}
                        disabled={!canRefine}
                    />
                </div>
            </div>
        </Panel>
    );
}
